import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useIndividualEntryRanking } from '../../api/hooks/useDvojbojRanking';
import {
  AGE_SUBCATEGORY_LABELS,
  AGE_SUBCATEGORY_ORDER,
} from '../../types/individualEntry';
import { formatTime } from '../../utils/timeFormat';
import type { Discipline } from '../../types/domain';
import type { IndividualEntryRankingItem } from '../../types/dvojboj';

interface Props {
  discipline: Discipline;
}

const MEDAL_COLORS = {
  1: { bg: 'rgba(255, 215, 0, 0.20)', text: '#7a5c00' },
  2: { bg: 'rgba(192, 192, 192, 0.28)', text: '#525252' },
  3: { bg: 'rgba(205, 127, 50, 0.22)', text: '#6b3a0c' },
} as const;

/**
 * Pořadí jednotlivců (samostatne prihlasky bez druzstva) v jedné disciplíně.
 * Frontend grupuje per `ageSubcategory` do mini-tabulek.
 */
export function IndividualEntryRankingTable({ discipline }: Props) {
  const { data, isLoading } = useIndividualEntryRanking(discipline.id);

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  const items = data ?? [];
  if (items.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Žádné výsledky jednotlivců v této disciplíně.
        </Typography>
      </Paper>
    );
  }

  // Group by ageSubcategory
  const groups = new Map<string, IndividualEntryRankingItem[]>();
  for (const item of items) {
    const key = item.ageSubcategory;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  const sortedKeys = Array.from(groups.keys()).sort(
    (a, b) =>
      AGE_SUBCATEGORY_ORDER.indexOf(a as never) -
      AGE_SUBCATEGORY_ORDER.indexOf(b as never),
  );

  return (
    <Stack spacing={3}>
      {sortedKeys.map((key) => (
        <IndividualEntryMiniTable
          key={key}
          ageSubcategory={key as never}
          items={groups.get(key)!}
          discipline={discipline}
        />
      ))}
    </Stack>
  );
}

function IndividualEntryMiniTable({
  ageSubcategory,
  items,
  discipline,
}: {
  ageSubcategory: keyof typeof AGE_SUBCATEGORY_LABELS;
  items: IndividualEntryRankingItem[];
  discipline: Discipline;
}) {
  const attempts = discipline.attemptsCount ?? 1;
  const fmt = discipline.timeFormat ?? 'SS_CC';

  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EmojiEventsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            {AGE_SUBCATEGORY_LABELS[ageSubcategory]}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({items.length}{' '}
            {items.length === 1 ? 'závodník' : items.length < 5 ? 'závodníci' : 'závodníků'})
          </Typography>
        </Stack>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, width: 80 }}>Pořadí</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Jméno</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>SDH</TableCell>
            {Array.from({ length: attempts }, (_, i) => (
              <TableCell key={i} align="right" sx={{ fontWeight: 700 }}>
                Pokus {i + 1}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Lepší čas
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const medal = item.rank ? MEDAL_COLORS[item.rank as 1 | 2 | 3] : undefined;
            return (
              <TableRow
                key={item.individualEntryId}
                sx={
                  medal
                    ? {
                        bgcolor: medal.bg,
                        '& td': { fontWeight: 600 },
                      }
                    : undefined
                }
              >
                <TableCell sx={{ fontWeight: 700, color: medal?.text }}>
                  {item.rank == null ? '—' : `${item.rank}.`}
                </TableCell>
                <TableCell>
                  {item.lastName} {item.firstName}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{item.sdhUnitName}</TableCell>
                {Array.from({ length: attempts }, (_, i) => (
                  <TableCell key={i} align="right" sx={{ fontFamily: 'monospace' }}>
                    {item.attemptTimesMs[i] != null ? (
                      formatTime(item.attemptTimesMs[i]!, fmt)
                    ) : item.isNp ? (
                      <Box component="span" sx={{ color: 'error.main' }}>
                        NP
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                  {item.bestTimeMs != null ? (
                    formatTime(item.bestTimeMs, fmt)
                  ) : item.isNp ? (
                    <Box component="span" sx={{ color: 'error.main' }}>
                      NP
                    </Box>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}
